"use client"

import { Trans } from "@lingui/react/macro"
import {
  Button,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  cn,
} from "@rarible_int/anaconda_ui_components"
import { reatomComponent } from "@reatom/react"
import {
  deriveBuybackRatePercent,
  formatBuybackRatePercent,
} from "./gacha-buyback-rate"
import { GachaProgressLabel } from "./gacha-progress-label"

export type GachaBuybackConfirmationItem = {
  image: string
  name: string
  collectionName?: string
  marketValue: string
  buybackOffer: string
  /** null when the payout and market value use incomparable units. */
  buybackRatePercent?: number | null
}

type GachaBuybackConfirmationBodyProps = {
  item: GachaBuybackConfirmationItem
  buybackBusy: boolean
  payoutLoading?: boolean
  confirmDisabled?: boolean
  buybackProgressDots?: string
  onCancel: () => void
  onConfirm: () => void
}

export const GachaBuybackConfirmationBody = reatomComponent(
  ({
    item,
    buybackBusy,
    payoutLoading = false,
    confirmDisabled = false,
    buybackProgressDots,
    onCancel,
    onConfirm,
  }: GachaBuybackConfirmationBodyProps) => {
    const buybackRatePercent =
      item.buybackRatePercent === null
        ? 0
        : item.buybackRatePercent ||
          deriveBuybackRatePercent(item.marketValue, item.buybackOffer)

    return (
      <>
        <DialogHeader>
          <DialogTitle className="font-headline font-normal text-2xl leading-tight tracking-tight text-foreground">
            <Trans>Accept Buyback Offer</Trans>
          </DialogTitle>
          <DialogDescription className="sr-only">
            <Trans>
              Confirm buyback offer payout before creating the buyback
              transaction.
            </Trans>
          </DialogDescription>
        </DialogHeader>

        <div className="flex w-full max-w-full items-center gap-3 overflow-hidden rounded-lg border border-foreground/10 bg-foreground/[0.02] p-3">
          <div className="relative aspect-square w-14 shrink-0 overflow-hidden rounded-md bg-foreground/[0.04]">
            <img
              src={item.image}
              alt=""
              className="h-full w-full object-contain"
            />
          </div>
          <div className="min-w-0 flex-1 overflow-hidden">
            <p className="block w-full max-w-full overflow-hidden text-ellipsis whitespace-nowrap text-sm text-foreground">
              {item.name}
            </p>
            {item.collectionName ? (
              <p className="mt-1 block w-full max-w-full overflow-hidden text-ellipsis whitespace-nowrap text-xs text-foreground/60">
                {item.collectionName}
              </p>
            ) : null}
          </div>
        </div>

        <div
          className="flex flex-col gap-3 rounded-lg border border-foreground/10 p-3 text-sm"
          data-testid="gacha-buyback-summary"
        >
          <BuybackSummaryRow
            label={<Trans>Market value</Trans>}
            value={item.marketValue}
          />
          <BuybackSummaryRow
            label={<Trans>Buyback rate</Trans>}
            value={formatBuybackRatePercent(buybackRatePercent)}
          />
          <div className="h-px bg-foreground/10" />
          <BuybackSummaryRow
            label={<Trans>Total payout</Trans>}
            value={
              payoutLoading ? <Trans>Loading quote…</Trans> : item.buybackOffer
            }
            strong
          />
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row sm:space-x-0">
          <Button
            type="button"
            variant="secondary"
            className="w-full sm:w-[112px]"
            disabled={buybackBusy}
            onClick={onCancel}
          >
            <Trans>Cancel</Trans>
          </Button>
          <Button
            type="button"
            variant="default"
            className="w-full sm:flex-1"
            disabled={buybackBusy || confirmDisabled}
            onClick={onConfirm}
          >
            <GachaProgressLabel
              label="Accept Buyback Offer"
              dots={buybackBusy ? buybackProgressDots || "." : undefined}
            />
          </Button>
        </DialogFooter>

        <p className="text-xs leading-5 text-foreground/50">
          <Trans>
            By accepting this buyback offer, you confirm that the collectible
            will be sold back at the quoted payout. Final settlement may vary if
            wallet or network requirements cannot be completed.
          </Trans>
        </p>
      </>
    )
  },
  "GachaBuybackConfirmationBody",
)

function BuybackSummaryRow({
  label,
  value,
  strong = false,
}: {
  label: React.ReactNode
  value: React.ReactNode
  strong?: boolean
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-foreground/60">{label}</span>
      <span className={cn("text-right", strong && "text-base text-brand")}>
        {value}
      </span>
    </div>
  )
}
